import React, { useState, useEffect } from 'react';
import {
  Modal, ModalOverlay, ModalContent, ModalHeader, ModalBody, ModalFooter,
  ModalCloseButton, Button, VStack, HStack, Input, Textarea, FormControl, FormLabel,
  Box, Text, Badge, useToast, Flex, IconButton, Select, Divider,
  Accordion, AccordionItem, AccordionButton, AccordionPanel, AccordionIcon,
} from '@chakra-ui/react';
import { FiPlus, FiEdit2, FiTrash2, FiX } from 'react-icons/fi';
import api from '../api';

export default function ResourceBookingModal({ projectId, taskId, isOpen, onClose }) {
  const [bookings, setBookings] = useState([]);
  const [resources, setResources] = useState([]);
  const [contacts, setContacts] = useState([]);
  const [companies, setCompanies] = useState([]);
  const [allUsers, setAllUsers] = useState([]);
  const [invoices, setInvoices] = useState([]);
  const [showForm, setShowForm] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const emptyForm = {
    title: '', description: '', start_date: '', start_time: '',
    end_date: '', end_time: '',
    company_id: '', status: 'confirmed', resource_ids: [], contact_ids: [], user_ids: [],
  };
  const [form, setForm] = useState(emptyForm);
  const [saving, setSaving] = useState(false);
  const toast = useToast();

  const fetchBookings = async () => {
    try {
      const taskParam = taskId ? `&task_id=${taskId}` : '';
      const { data } = await api.get(`/resource-bookings?project_id=${projectId}${taskParam}`);
      setBookings(data);
    } catch(e) {}
  };
  const fetchResources = async () => { try { const { data } = await api.get('/resources'); setResources(data); } catch(e) {} };
  const fetchMeta = async () => {
    try {
      const [ctRes, coRes, usrRes, invRes] = await Promise.all([
        api.get(`/projects/${projectId}/contacts`).catch(() => ({ data: [] })),
        api.get(`/projects/${projectId}/companies`).catch(() => ({ data: [] })),
        api.get('/calendar/users').catch(() => ({ data: [] })),
        api.get(`/invoices?project_id=${projectId}&direction=out`).catch(() => ({ data: [] })),
      ]);
      setContacts(ctRes.data || []);
      setCompanies(coRes.data || []);
      setAllUsers(usrRes.data || []);
      setInvoices(Array.isArray(invRes.data) ? invRes.data : (invRes.data?.data || []));
    } catch(e) {}
  };

  useEffect(() => {
    if (isOpen && projectId) { fetchBookings(); fetchResources(); fetchMeta(); setShowForm(false); setEditingId(null); }
  }, [isOpen, projectId]); // eslint-disable-line

  const openEdit = (b) => {
    setForm({
      title: b.title || '', description: b.description || '',
      start_date: b.start_date ? b.start_date.split('T')[0] : '', start_time: b.start_time?.slice(0,5) || '',
      end_date: b.end_date ? b.end_date.split('T')[0] : '', end_time: b.end_time?.slice(0,5) || '',
      company_id: b.company_id || '', status: b.status || 'confirmed',
      resource_ids: (b.resources || []).map(r => r.resource_id),
      contact_ids: (b.contacts || []).map(c => c.contact_id),
      user_ids: (b.users || []).map(u => u.user_id),
    });
    setEditingId(b.id); setShowForm(true);
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      const payload = { ...form, project_id: projectId, task_id: taskId || null, company_id: form.company_id || null };
      if (editingId) await api.put(`/resource-bookings/${editingId}`, payload);
      else await api.post('/resource-bookings', payload);
      await fetchBookings(); setShowForm(false); setEditingId(null); setForm(emptyForm);
      toast({ title: editingId ? 'Booking updated' : 'Booking created', status: 'success', duration: 1500 });
    } catch(e) { toast({ title: 'Failed: ' + (e.response?.data?.error || e.message), status: 'error', duration: 3000 }); }
    finally { setSaving(false); }
  };

  const toggleItem = (field, id) => {
    setForm(p => ({ ...p, [field]: p[field].includes(id) ? p[field].filter(x => x !== id) : [...p[field], id] }));
  };

  const linkInvoice = async (bookingId, invoiceId) => {
    try { await api.put(`/resource-bookings/${bookingId}/invoice`, { invoice_id: invoiceId || null }); await fetchBookings(); toast({ title: 'Invoice linked', status: 'success', duration: 1500 }); } catch(e) {}
  };

  const fmtDate = (d) => d ? new Date(d.split('T')[0] + 'T12:00:00').toLocaleDateString('en-GB', { day:'numeric', month:'short' }) : '';
  const fmtTime = (t) => t ? t.slice(0,5) : '';
  const statusColors = { confirmed: 'green', tentative: 'yellow', cancelled: 'red' };

  return (
    <Modal isOpen={isOpen} onClose={onClose} size="xl" scrollBehavior="inside">
      <ModalOverlay /><ModalContent maxH="85vh">
        <ModalHeader fontSize="md">
          <Flex justify="space-between" pr={8}>
            Resource Bookings
            <Button size="xs" leftIcon={<FiPlus />} colorScheme="teal" variant={showForm ? 'solid' : 'outline'}
              onClick={() => { setShowForm(!showForm); setEditingId(null); setForm(emptyForm); }}>
              {showForm && !editingId ? 'Cancel' : 'New Booking'}
            </Button>
          </Flex>
        </ModalHeader><ModalCloseButton />
        <ModalBody>
          {showForm && (
            <Box bg="teal.50" p={3} borderRadius="md" mb={4}>
              <VStack spacing={2}>
                <FormControl><FormLabel fontSize="xs" mb={0.5}>Title</FormLabel>
                  <Input size="sm" value={form.title} onChange={e => setForm(p => ({ ...p, title: e.target.value }))} placeholder="Booking title" /></FormControl>
                <HStack w="100%" spacing={2}>
                  <FormControl flex={1}><FormLabel fontSize="xs" mb={0.5}>Start Date</FormLabel>
                    <Input size="sm" type="date" value={form.start_date} onChange={e => setForm(p => ({ ...p, start_date: e.target.value }))} /></FormControl>
                  <FormControl flex={1}><FormLabel fontSize="xs" mb={0.5}>Start Time</FormLabel>
                    <Input size="sm" type="time" value={form.start_time} onChange={e => setForm(p => ({ ...p, start_time: e.target.value }))} /></FormControl>
                </HStack>
                <HStack w="100%" spacing={2}>
                  <FormControl flex={1}><FormLabel fontSize="xs" mb={0.5}>End Date</FormLabel>
                    <Input size="sm" type="date" value={form.end_date} onChange={e => setForm(p => ({ ...p, end_date: e.target.value }))} /></FormControl>
                  <FormControl flex={1}><FormLabel fontSize="xs" mb={0.5}>End Time</FormLabel>
                    <Input size="sm" type="time" value={form.end_time} onChange={e => setForm(p => ({ ...p, end_time: e.target.value }))} /></FormControl>
                </HStack>
                <HStack w="100%" spacing={2}>
                  <FormControl flex={1}><FormLabel fontSize="xs" mb={0.5}>Company</FormLabel>
                    <Select size="sm" value={form.company_id} onChange={e => setForm(p => ({ ...p, company_id: e.target.value }))}>
                      <option value="">None</option>
                      {companies.map(c => <option key={c.company_id || c.id} value={c.company_id || c.id}>{c.name}</option>)}
                    </Select></FormControl>
                  <FormControl flex={1}><FormLabel fontSize="xs" mb={0.5}>Status</FormLabel>
                    <Select size="sm" value={form.status} onChange={e => setForm(p => ({ ...p, status: e.target.value }))}>
                      <option value="confirmed">Confirmed</option><option value="tentative">Tentative</option>
                      <option value="cancelled">Cancelled</option>
                    </Select></FormControl>
                </HStack>
                <FormControl><FormLabel fontSize="xs" mb={0.5}>Description</FormLabel>
                  <Textarea size="sm" rows={2} value={form.description} onChange={e => setForm(p => ({ ...p, description: e.target.value }))} /></FormControl>
                <FormControl><FormLabel fontSize="xs" mb={0.5}>Contacts</FormLabel>
                  <Flex gap={1} flexWrap="wrap">
                    {contacts.map(c => (
                      <Badge key={c.id} cursor="pointer" fontSize="10px" px={2} py={0.5}
                        colorScheme={form.contact_ids.includes(c.id) ? 'blue' : 'gray'} variant={form.contact_ids.includes(c.id) ? 'solid' : 'outline'}
                        onClick={() => toggleItem('contact_ids', c.id)}>{c.first_name} {c.last_name || ''}</Badge>
                    ))}
                    {contacts.length === 0 && <Text fontSize="10px" color="gray.400">No contacts on project</Text>}
                  </Flex></FormControl>
                <FormControl><FormLabel fontSize="xs" mb={0.5}>Team Members</FormLabel>
                  <Flex gap={1} flexWrap="wrap">
                    {allUsers.map(u => (
                      <Badge key={u.id} cursor="pointer" fontSize="10px" px={2} py={0.5}
                        colorScheme={form.user_ids.includes(u.id) ? 'purple' : 'gray'} variant={form.user_ids.includes(u.id) ? 'solid' : 'outline'}
                        onClick={() => toggleItem('user_ids', u.id)}>{u.display_name || u.username}</Badge>
                    ))}
                  </Flex></FormControl>
                <FormControl><FormLabel fontSize="xs" mb={0.5}>Resources</FormLabel>
                  <Flex gap={1} flexWrap="wrap">
                    {resources.map(r => (
                      <Badge key={r.id} cursor="pointer" fontSize="10px" px={2} py={0.5}
                        colorScheme={form.resource_ids.includes(r.id) ? (r.is_person ? 'purple' : 'teal') : 'gray'}
                        variant={form.resource_ids.includes(r.id) ? 'solid' : 'outline'}
                        onClick={() => toggleItem('resource_ids', r.id)}>
                        {r.is_person ? '👤 ' : ''}{r.resource_code ? `${r.resource_code} — ` : ''}{r.name}
                      </Badge>
                    ))}
                    {resources.length === 0 && <Text fontSize="10px" color="gray.400">No resources configured</Text>}
                  </Flex></FormControl>
                <Button size="xs" colorScheme="teal" onClick={handleSave} isLoading={saving} alignSelf="flex-end">
                  {editingId ? 'Update' : 'Create Booking'}</Button>
              </VStack>
            </Box>
          )}
          {bookings.length === 0 ? (
            <Text fontSize="sm" color="gray.500" textAlign="center" py={8}>No resource bookings yet.</Text>
          ) : (
            <Accordion allowMultiple>
              {bookings.map(b => (
                <AccordionItem key={b.id} border="1px" borderColor="gray.100" borderRadius="md" mb={2}>
                  <AccordionButton py={2} px={3}>
                    <Flex flex={1} align="center" gap={2} fontSize="12px">
                      <Badge colorScheme={statusColors[b.status]} fontSize="8px">{b.status}</Badge>
                      <Text fontWeight="600" color="gray.600" flexShrink={0}>{fmtDate(b.start_date)}</Text>
                      {b.start_time && <Text color="gray.500">{fmtTime(b.start_time)}{b.end_time ? `–${fmtTime(b.end_time)}` : ''}</Text>}
                      <Text fontWeight="500" noOfLines={1}>{b.title || b.task_title || '(untitled)'}</Text>
                    </Flex>
                    <AccordionIcon />
                  </AccordionButton>
                  <AccordionPanel pb={3} px={3}>
                    {b.description && <Text fontSize="11px" color="gray.600" mb={2}>{b.description}</Text>}
                    <HStack spacing={3} fontSize="10px" color="gray.500" mb={1} flexWrap="wrap">
                      {b.company_name && <Text>Company: <b>{b.company_name}</b></Text>}
                      {b.project_name && <Text>Project: <b>{b.project_name}</b></Text>}
                    </HStack>
                    {(b.contacts || []).length > 0 && (
                      <Flex gap={1} mb={1} flexWrap="wrap" align="center">
                        <Text fontSize="9px" color="gray.400" fontWeight="700">CONTACTS:</Text>
                        {b.contacts.map(c => <Badge key={c.contact_id} colorScheme="blue" fontSize="8px" variant="subtle">{c.first_name} {c.last_name || ''}</Badge>)}
                      </Flex>
                    )}
                    {(b.users || []).length > 0 && (
                      <Flex gap={1} mb={1} flexWrap="wrap" align="center">
                        <Text fontSize="9px" color="gray.400" fontWeight="700">TEAM:</Text>
                        {b.users.map(u => <Badge key={u.user_id} colorScheme="purple" fontSize="8px" variant="subtle">{u.display_name}</Badge>)}
                      </Flex>
                    )}
                    {(b.resources || []).length > 0 && (
                      <Flex gap={1} mb={2} flexWrap="wrap" align="center">
                        <Text fontSize="9px" color="gray.400" fontWeight="700">RESOURCES:</Text>
                        {b.resources.map(r => <Badge key={r.resource_id} colorScheme="teal" fontSize="8px">{r.resource_code || r.resource_name}</Badge>)}
                      </Flex>
                    )}
                    <Divider mb={2} />
                    <HStack spacing={2} fontSize="10px">
                      {b.invoice_id ? (
                        <HStack>
                          <Badge colorScheme="green" fontSize="9px">Invoice: {b.invoice_number || `#${b.invoice_id}`} (£{parseFloat(b.invoice_total || 0).toFixed(2)})</Badge>
                          <IconButton icon={<FiX />} size="xs" variant="ghost" colorScheme="red" onClick={() => linkInvoice(b.id, null)} aria-label="Unlink" />
                        </HStack>
                      ) : (
                        <Select size="xs" w="220px" placeholder="Link invoice..." value=""
                          onChange={e => { if (e.target.value) linkInvoice(b.id, parseInt(e.target.value)); }}>
                          {invoices.map(inv => <option key={inv.id} value={inv.id}>{inv.invoice_number || `#${inv.id}`} — £{parseFloat(inv.total_gross || 0).toFixed(2)}</option>)}
                        </Select>
                      )}
                    </HStack>
                    <HStack mt={2} spacing={1}>
                      <Button size="xs" variant="ghost" leftIcon={<FiEdit2 />} onClick={() => openEdit(b)}>Edit</Button>
                      <Button size="xs" variant="ghost" colorScheme="red" leftIcon={<FiTrash2 />}
                        onClick={async () => { await api.delete(`/resource-bookings/${b.id}`); fetchBookings(); }}>Delete</Button>
                    </HStack>
                  </AccordionPanel>
                </AccordionItem>
              ))}
            </Accordion>
          )}
        </ModalBody>
        <ModalFooter><Button size="sm" onClick={onClose}>Close</Button></ModalFooter>
      </ModalContent>
    </Modal>
  );
}
